import { getCurrentUser, fetchAuthSession, signIn, signOut, signUp, confirmSignUp, resendSignUpCode, updateUserAttributes, updatePassword } from 'aws-amplify/auth';

import { clearTokenCache } from './auth';

function resolveCognitoEndpoint(): string {
  const explicit = import.meta.env.VITE_COGNITO_ENDPOINT as string | undefined;
  if (explicit) return explicit;
  return window.location.origin + '/cognito';
}

export interface CognitoAuthConfig {
  userPoolId: string;
  userPoolClientId: string;
  userPoolEndpoint: string;
  cognitoDomain?: string;
  isLocal: boolean;
}

export function getCognitoAuthConfig(): CognitoAuthConfig {
  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID as string | undefined;
  const userPoolClientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined;

  if (!userPoolId || !userPoolClientId) {
    throw new Error(
      'Missing Cognito configuration. Set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID.'
    );
  }

  const userPoolEndpoint = resolveCognitoEndpoint();
  const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN as string | undefined;
  const isLocal = !cognitoDomain;

  return { userPoolId, userPoolClientId, userPoolEndpoint, cognitoDomain, isLocal };
}

let _config: CognitoAuthConfig | null = null;
export function ensureCognitoConfig(): CognitoAuthConfig {
  _config ??= getCognitoAuthConfig();
  return _config;
}

export const cognitoAuth = {
  async signIn(email: string, password: string) {
    const cfg = ensureCognitoConfig();
    return await signIn({
      username: email,
      password,
      ...(cfg.isLocal
        ? { options: { authFlowType: 'USER_PASSWORD_AUTH' as const } }
        : {}),
    });
  },

  async signUp(email: string, password: string) {
    return await signUp({ username: email, password, options: { userAttributes: { email } } });
  },

  async confirmSignUp(email: string, code: string) {
    await confirmSignUp({ username: email, confirmationCode: code });
  },

  async resendConfirmationCode(email: string) {
    await resendSignUpCode({ username: email });
  },

  async signOut() {
    clearTokenCache();
    await signOut();
  },

  async globalSignOut() {
    clearTokenCache();
    await signOut({ global: true });
  },

  async updateEmail(newEmail: string) {
    await updateUserAttributes({
      userAttributes: { email: newEmail },
    });
  },

  async changePassword(oldPassword: string, newPassword: string) {
    await updatePassword({ oldPassword, newPassword });
  },

  async getCurrentUser() {
    try { return await getCurrentUser(); }
    catch { return null; }
  },

  async getAccessToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() ?? null;
    } catch { return null; }
  },

  async getIdToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() ?? null;
    } catch { return null; }
  },

  async getGroups(): Promise<string[]> {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken;
      if (!token) return [];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const parsed: Record<string, unknown> = JSON.parse(atob(token.toString().split('.')[1]));
      const rawGroups = parsed['cognito:groups'];
      return Array.isArray(rawGroups) ? rawGroups.filter((g): g is string => typeof g === 'string') : [];
    } catch { return []; }
  },
};
